import type { CalendarEvent, CalendarEventAttendee, CalendarEventModality } from '../types';

/**
 * Raw shape of `GET /api/calendar/events`. `start`/`end` are DISCRIMINATED:
 * absent fields are omitted from the JSON entirely (not null). Read `allDay`
 * first and only the keys that apply — never assume all three exist.
 */
export interface RawExternalCalendarEventDate {
  /** ISO 8601 UTC — only present when allDay === false */
  instant?: string;
  /** IANA — only present when allDay === false */
  timeZoneId?: string;
  /** "yyyy-MM-dd" — only present when allDay === true */
  date?: string;
}

/** Raw shape of `CalendarEventAttendeeDto` — present on every source, camelCase (System.Text.Json Web defaults). */
export interface RawCalendarEventAttendee {
  userId: string | null;
  email: string;
  displayName: string;
  isOrganizer: boolean;
  isOptional: boolean;
  responseStatus: 'needsaction' | 'accepted' | 'declined' | 'tentative';
}

export interface RawExternalCalendarEvent {
  id: string;
  title: string;
  description: string | null;
  location: string | null;
  source: 'google' | 'microsoft';
  type: 'external';
  allDay: boolean;
  start: RawExternalCalendarEventDate;
  end: RawExternalCalendarEventDate;
  status: 'confirmed' | 'tentative' | 'cancelled';
  sensitivity: 'default' | 'private' | 'confidential';
  organizerEmail: string | null;
  webLink: string | null;
  meetingUrl: string | null;
  calendarId: string;
  calendarName: string;
  modality: CalendarEventModality | null;
  attendees: RawCalendarEventAttendee[];
}

/**
 * Native TimeOff absences, surfaced through the same `GET /calendar/events`
 * feed as external-provider events — distinguished only by `source`/`type`
 * (see `2026-08-03-timeoff-frontend-integration.md`, "Calendar Integration").
 * Meeting-only fields (`status`, `organizerEmail`, etc.) don't apply here.
 */
export interface RawNativeCalendarEvent {
  id: string;
  title: string;
  description: string | null;
  location: string | null;
  source: 'khronox';
  type: 'native';
  allDay: boolean;
  start: RawExternalCalendarEventDate;
  end: RawExternalCalendarEventDate;
  modality: CalendarEventModality | null;
  attendees: RawCalendarEventAttendee[];
}

/**
 * User-created "quick marker" events (milestone / time-entry), owned by the
 * caller — surfaced through the same unified `GET /calendar/events` feed
 * (see `backend-requirement-calendar-event-crud.md`). Only these carry
 * `source: 'internal'` and are targetable by the create/update/delete
 * endpoints in `internal-calendar-events.service.ts`.
 */
export interface RawInternalCalendarEvent {
  id: string;
  title: string;
  description: string | null;
  location: string | null;
  source: 'internal';
  type: 'milestone' | 'time-entry';
  allDay: boolean;
  start: RawExternalCalendarEventDate;
  end: RawExternalCalendarEventDate;
  modality: CalendarEventModality | null;
  attendees: RawCalendarEventAttendee[];
}

export type RawCalendarEvent =
  | RawExternalCalendarEvent
  | RawNativeCalendarEvent
  | RawInternalCalendarEvent;

function toDate(part: RawExternalCalendarEventDate, allDay: boolean): Date {
  if (allDay) {
    // end is treated as INCLUSIVE (same visible day), replicating the existing
    // convention of task.adapter.ts for allDay events. Known pre-existing
    // limitation (react-big-calendar expects exclusive end for multi-day
    // spans) — deliberately NOT fixed here, out of scope of this integration.
    return new Date(`${part.date}T00:00:00`);
  }
  return new Date(part.instant!);
}

// `raw.attendees`/`raw.modality` defensively tolerate being absent — the POST
// /calendar/events response reuses the same DTO as GET, but that hasn't been
// confirmed field-for-field, so a missing array here must not throw.
function toAttendees(raw: RawCalendarEventAttendee[] | undefined): CalendarEventAttendee[] {
  if (!Array.isArray(raw)) return [];
  return raw.map((a) => ({
    userId: a.userId,
    email: a.email,
    displayName: a.displayName,
    isOrganizer: a.isOrganizer,
    isOptional: a.isOptional,
    responseStatus: a.responseStatus,
  }));
}

export function externalCalendarEventToCalendarEvent(raw: RawCalendarEvent): CalendarEvent {
  const base = {
    id: raw.id,
    title: raw.title,
    start: toDate(raw.start, raw.allDay),
    end: toDate(raw.end, raw.allDay),
    allDay: raw.allDay,
  };

  if (raw.type === 'native') {
    return {
      ...base,
      type: 'native',
      source: raw.source,
      metadata: {
        description: raw.description,
        location: raw.location,
        modality: raw.modality ?? null,
        attendees: toAttendees(raw.attendees),
      },
    };
  }

  if (raw.source === 'internal') {
    return {
      ...base,
      type: raw.type,
      source: 'internal',
      metadata: {
        description: raw.description,
        location: raw.location,
        modality: raw.modality ?? null,
        attendees: toAttendees(raw.attendees),
      },
    };
  }

  return {
    ...base,
    type: 'external',
    source: raw.source,
    metadata: {
      description: raw.description,
      location: raw.location,
      status: raw.status,
      sensitivity: raw.sensitivity,
      organizerEmail: raw.organizerEmail,
      webLink: raw.webLink,
      meetingUrl: raw.meetingUrl,
      calendarId: raw.calendarId,
      calendarName: raw.calendarName,
      modality: raw.modality ?? null,
      attendees: toAttendees(raw.attendees),
    },
  };
}
